/**
 * @file payslipPdfGenerator.js
 * @description Payslip PDF generation utility using the browser's print API.
 *
 * Exports:
 *  - generatePayslipHtml(payslipData)  — returns a print-ready HTML string
 *  - generatePayslipPdf(payslipData)   — opens a print dialog for the payslip
 *  - generateBulkPayslipsHtml(salariesList) — returns bulk print-ready HTML string
 *  - generateBulkPayslipsPdf(salariesList)  — opens a bulk print dialog
 */

/**
 * Process raw payslip record to align with the "Review Salaries" structure.
 * @param {Object} payslipData
 * @returns {Object} Cleaned/mapped payslip data
 */
function processPayslipData(payslipData) {
  const {
    companyName,
    companyAddress,
    employeeName,
    employeeId,
    designation,
    department,
    payrollMonth,
    earnings = [],
    deductions = [],
    grossSalary = 0,
    totalDeductions,
    netSalary,
    employerPf,
  } = payslipData || {};

  // Exclude Variable Pay from main earnings table as it has its own premium section
  const filteredEarnings = (earnings || []).filter((e) => e?.name !== "Variable Pay");

  // Extract Variable Pay amount
  const variablePayAmount = Number(
    payslipData?.variable_amount ||
      payslipData?.variablePay ||
      (earnings || []).find((e) => e?.name === "Variable Pay")?.amount ||
      0,
  );

  const rawDeductions = deductions || [];
  const mappedDeductions = [];
  const processedKeys = new Set();

  // Clean and filter deductions to match: PF, ESIC, PT, TDS, and other custom deductions (excluding employer ones)
  rawDeductions.forEach((d) => {
    const nameLower = (d.name || "").toLowerCase().trim();
    if (nameLower.includes("employer")) return;

    if (nameLower === "pf (employee)" || nameLower === "pf") {
      mappedDeductions.push({ name: "PF", amount: d.amount });
      processedKeys.add("pf");
    } else if (nameLower === "esic (employee)" || nameLower === "esic") {
      mappedDeductions.push({ name: "ESIC", amount: d.amount });
      processedKeys.add("esic");
    } else if (
      nameLower === "professional tax" ||
      nameLower === "pt" ||
      nameLower === "professionaltax"
    ) {
      mappedDeductions.push({ name: "PT", amount: d.amount });
      processedKeys.add("pt");
    } else if (nameLower === "tds") {
      mappedDeductions.push({ name: "TDS", amount: d.amount });
      processedKeys.add("tds");
    } else {
      mappedDeductions.push(d);
    }
  });

  // Inject standard deductions if flat fields exist on the payload but not present in mappedDeductions
  if (!processedKeys.has("pf")) {
    const pfAmt = payslipData?.pfEmployee ?? payslipData?.pf;
    if (pfAmt !== undefined && pfAmt !== null) {
      mappedDeductions.push({ name: "PF", amount: pfAmt });
    }
  }
  if (!processedKeys.has("esic")) {
    const esicAmt = payslipData?.esic;
    if (esicAmt !== undefined && esicAmt !== null) {
      mappedDeductions.push({ name: "ESIC", amount: esicAmt });
    }
  }
  if (!processedKeys.has("pt")) {
    const ptAmt = payslipData?.pt;
    if (ptAmt !== undefined && ptAmt !== null) {
      mappedDeductions.push({ name: "PT", amount: ptAmt });
    }
  }
  if (!processedKeys.has("tds")) {
    const tdsAmt = payslipData?.tds;
    if (tdsAmt !== undefined && tdsAmt !== null) {
      mappedDeductions.push({ name: "TDS", amount: tdsAmt });
    }
  }

  // Calculate clean total of employee-side deductions
  const calculatedTotalDeductions = mappedDeductions.reduce(
    (sum, d) => sum + Number(d.amount || 0),
    0,
  );
  const displayTotalDeductions =
    totalDeductions !== undefined ? totalDeductions : calculatedTotalDeductions;
  const displayNetSalary =
    netSalary !== undefined ? netSalary : grossSalary - calculatedTotalDeductions;

  // Fallback to extract employer PF from raw deductions if not provided as flat field
  let displayEmployerPf = employerPf;
  if (displayEmployerPf === undefined || displayEmployerPf === null || displayEmployerPf === 0) {
    const foundPfEmployer = rawDeductions.find(
      (d) => (d.name || "").toLowerCase().trim() === "pf (employer)",
    );
    if (foundPfEmployer) {
      displayEmployerPf = foundPfEmployer.amount;
    }
  }

  return {
    companyName,
    companyAddress,
    employeeName,
    employeeId,
    designation,
    department,
    payrollMonth,
    earnings: filteredEarnings,
    deductions: mappedDeductions,
    grossSalary,
    totalDeductions: displayTotalDeductions,
    netSalary: displayNetSalary,
    employerPf: displayEmployerPf,
    variablePayAmount,
  };
}

/**
 * Generates a print-ready HTML string for a payslip.
 * @param {Object} payslipData
 * @returns {string} Complete HTML document string
 */
export function generatePayslipHtml(payslipData) {
  const formatINR = (val) => `₹${Number(val || 0).toLocaleString("en-IN")}`;

  const {
    companyName,
    companyAddress,
    employeeName,
    employeeId,
    designation,
    department,
    payrollMonth,
    earnings,
    deductions,
    grossSalary,
    totalDeductions,
    netSalary,
    employerPf,
    variablePayAmount,
  } = processPayslipData(payslipData);

  const variablePayHtml =
    variablePayAmount > 0
      ? `
  <div class="variable-pay-card">
    <div class="variable-pay-title">VARIABLE PAY <span class="variable-pay-sub">(Performance / Incentive Pay)</span></div>
    <div class="variable-pay-amount">${formatINR(variablePayAmount)}</div>
  </div>
  `
      : "";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Payslip - ${employeeName || "Employee"} - ${payrollMonth || ""}</title>
  <style>
    @page { size: A4; margin: 20mm; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 0; }
    .header { text-align: center; border-bottom: 2px solid #6366f1; padding-bottom: 12px; margin-bottom: 16px; }
    .company-name { font-size: 18px; font-weight: bold; color: #6366f1; }
    .company-address { font-size: 11px; color: #666; margin-top: 4px; }
    .payslip-title { font-size: 14px; font-weight: bold; margin-top: 8px; }
    .employee-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; padding: 12px; background: #f8f9fa; border-radius: 6px; }
    .info-row { display: flex; gap: 8px; }
    .info-label { font-weight: bold; color: #555; min-width: 100px; }
    .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; color: #6366f1; margin: 12px 0 6px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f1f5f9; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.04em; color: #555; }
    td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
    td:last-child { text-align: right; font-weight: 600; }
    .gross-row td { font-weight: bold; background: #f8f9fa; }
    .net-row td { font-weight: bold; font-size: 14px; background: #ede9fe; color: #6366f1; }
    .variable-pay-card { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-radius: 6px; background: #f5f3ff; border: 1px solid #ddd6fe; margin: 14px 0; }
    .variable-pay-title { font-size: 11px; font-weight: bold; color: #6366f1; text-transform: uppercase; letter-spacing: 0.05em; }
    .variable-pay-sub { font-size: 10px; color: #666; font-weight: normal; text-transform: none; }
    .variable-pay-amount { font-size: 13px; font-weight: bold; color: #6366f1; }
    .employer-pf { font-size: 11px; color: #666; margin-top: 8px; padding: 6px 10px; background: #f0fdf4; border-radius: 4px; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #999; text-align: center; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">${companyName || "Your Company"}</div>
    <div class="company-address">${companyAddress || ""}</div>
    <div class="payslip-title">SALARY SLIP — ${payrollMonth || ""}</div>
  </div>
  
  <div class="employee-info">
    <div class="info-row"><span class="info-label">Employee Name:</span><span>${employeeName || ""}</span></div>
    <div class="info-row"><span class="info-label">Employee ID:</span><span>${employeeId || ""}</span></div>
    <div class="info-row"><span class="info-label">Designation:</span><span>${designation || ""}</span></div>
    <div class="info-row"><span class="info-label">Department:</span><span>${department || ""}</span></div>
  </div>

  <div class="section-title">Earnings</div>
  <table>
    <thead><tr><th>Component</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>
      ${earnings.map((e) => `<tr><td>${e.name}</td><td>${formatINR(e.amount)}</td></tr>`).join("")}
      <tr class="gross-row"><td>Gross Salary</td><td>${formatINR(grossSalary)}</td></tr>
    </tbody>
  </table>

  <div class="section-title">Deductions</div>
  <table>
    <thead><tr><th>Component</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>
      ${deductions.map((d) => `<tr><td>${d.name}</td><td>${formatINR(d.amount)}</td></tr>`).join("")}
      <tr class="gross-row"><td>Total Deductions</td><td>${formatINR(totalDeductions)}</td></tr>
    </tbody>
  </table>

  ${variablePayHtml}

  <table style="margin-top:12px">
    <tbody>
      <tr class="net-row"><td>NET SALARY</td><td>${formatINR(netSalary)}</td></tr>
    </tbody>
  </table>

  ${employerPf ? `<div class="employer-pf">Employer PF Contribution (informational): ${formatINR(employerPf)}</div>` : ""}

  <div class="footer">This is a computer-generated payslip and does not require a signature.</div>
</body>
</html>`;
}

/**
 * Generates and downloads a payslip PDF by opening a print dialog.
 * Creates a hidden iframe, injects the HTML, and triggers window.print().
 * @param {Object} payslipData
 */
export function generatePayslipPdf(payslipData) {
  const html = generatePayslipHtml(payslipData);

  // Create a hidden iframe for printing
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow.focus();

  // Give browser time to render before printing
  setTimeout(() => {
    iframe.contentWindow.print();
    // Clean up after print dialog closes
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 500);
}

/**
 * Generates print-ready HTML for multiple payslips separated by page breaks.
 * @param {Array<Object>} salariesList
 * @returns {string} Complete HTML document string
 */
export function generateBulkPayslipsHtml(salariesList) {
  const formatINR = (val) => `₹${Number(val || 0).toLocaleString("en-IN")}`;

  const payslipsHtml = salariesList
    .map((payslipData) => {
      const {
        companyName,
        companyAddress,
        employeeName,
        employeeId,
        designation,
        department,
        payrollMonth,
        earnings,
        deductions,
        grossSalary,
        totalDeductions,
        netSalary,
        employerPf,
        variablePayAmount,
      } = processPayslipData(payslipData);

      const variablePayHtml =
        variablePayAmount > 0
          ? `
    <div class="variable-pay-card">
      <div class="variable-pay-title">VARIABLE PAY <span class="variable-pay-sub">(Performance / Incentive Pay)</span></div>
      <div class="variable-pay-amount">${formatINR(variablePayAmount)}</div>
    </div>
    `
          : "";

      return `
  <div class="payslip-container">
    <div class="header">
      <div class="company-name">${companyName || "Your Company"}</div>
      <div class="company-address">${companyAddress || ""}</div>
      <div class="payslip-title">SALARY SLIP — ${payrollMonth || ""}</div>
    </div>
    
    <div class="employee-info">
      <div class="info-row"><span class="info-label">Employee Name:</span><span>${employeeName || ""}</span></div>
      <div class="info-row"><span class="info-label">Employee ID:</span><span>${employeeId || ""}</span></div>
      <div class="info-row"><span class="info-label">Designation:</span><span>${designation || ""}</span></div>
      <div class="info-row"><span class="info-label">Department:</span><span>${department || ""}</span></div>
    </div>

    <div class="section-title">Earnings</div>
    <table>
      <thead><tr><th>Component</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>
        ${earnings.map((e) => `<tr><td>${e.name}</td><td>${formatINR(e.amount)}</td></tr>`).join("")}
        <tr class="gross-row"><td>Gross Salary</td><td>${formatINR(grossSalary)}</td></tr>
      </tbody>
    </table>

    <div class="section-title">Deductions</div>
    <table>
      <thead><tr><th>Component</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>
        ${deductions.map((d) => `<tr><td>${d.name}</td><td>${formatINR(d.amount)}</td></tr>`).join("")}
        <tr class="gross-row"><td>Total Deductions</td><td>${formatINR(totalDeductions)}</td></tr>
      </tbody>
    </table>

    ${variablePayHtml}

    <table style="margin-top:12px">
      <tbody>
        <tr class="net-row"><td>NET SALARY</td><td>${formatINR(netSalary)}</td></tr>
      </tbody>
    </table>

    ${employerPf ? `<div class="employer-pf">Employer PF Contribution (informational): ${formatINR(employerPf)}</div>` : ""}

    <div class="footer">This is a computer-generated payslip and does not require a signature.</div>
  </div>
    `;
    })
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>All Payslips - HR Fusions</title>
  <style>
    @page { size: A4; margin: 20mm; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 0; }
    .header { text-align: center; border-bottom: 2px solid #6366f1; padding-bottom: 12px; margin-bottom: 16px; }
    .company-name { font-size: 18px; font-weight: bold; color: #6366f1; }
    .company-address { font-size: 11px; color: #666; margin-top: 4px; }
    .payslip-title { font-size: 14px; font-weight: bold; margin-top: 8px; }
    .employee-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; padding: 12px; background: #f8f9fa; border-radius: 6px; }
    .info-row { display: flex; gap: 8px; }
    .info-label { font-weight: bold; color: #555; min-width: 100px; }
    .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; color: #6366f1; margin: 12px 0 6px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f1f5f9; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.04em; color: #555; }
    td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
    td:last-child { text-align: right; font-weight: 600; }
    .gross-row td { font-weight: bold; background: #f8f9fa; }
    .net-row td { font-weight: bold; font-size: 14px; background: #ede9fe; color: #6366f1; }
    .variable-pay-card { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-radius: 6px; background: #f5f3ff; border: 1px solid #ddd6fe; margin: 14px 0; }
    .variable-pay-title { font-size: 11px; font-weight: bold; color: #6366f1; text-transform: uppercase; letter-spacing: 0.05em; }
    .variable-pay-sub { font-size: 10px; color: #666; font-weight: normal; text-transform: none; }
    .variable-pay-amount { font-size: 13px; font-weight: bold; color: #6366f1; }
    .employer-pf { font-size: 11px; color: #666; margin-top: 8px; padding: 6px 10px; background: #f0fdf4; border-radius: 4px; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #999; text-align: center; }
    
    .payslip-container {
      page-break-after: always;
      break-after: page;
    }
    .payslip-container:last-child {
      page-break-after: avoid;
      break-after: avoid;
    }
  </style>
</head>
<body>
  ${payslipsHtml}
</body>
</html>`;
}

/**
 * Generates and downloads a bulk payslip PDF (single print job with multiple pages).
 * Creates a hidden iframe, injects the combined HTML, and triggers window.print().
 * @param {Array<Object>} salariesList
 */
export function generateBulkPayslipsPdf(salariesList) {
  const html = generateBulkPayslipsHtml(salariesList);

  // Create a hidden iframe for printing
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow.focus();

  // Give browser time to render before printing
  setTimeout(() => {
    iframe.contentWindow.print();
    // Clean up after print dialog closes
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 500);
}
